import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { apiService } from '@/services/api';
import { ContentNodeResponse, ContentNodeTranslation } from '@/types';
import { LanguageMenu } from './LanguageMenu';
import { UserMenu } from './UserMenu';
import { DashboardNav } from './DashboardNav';
import { ThemeToggle } from './ThemeToggle';
import { useLanguageSwitcher } from '../utils/i18n';
import { SUPPORTED_LOCALES } from '../config/shellConfig';
import { useSiteBranding } from '../hooks/useSiteBranding';

function pickTitle(node: ContentNodeResponse, locale: string): string {
  const t = node.translations.find((t: ContentNodeTranslation) => t.locale === locale && t.published)
    ?? node.translations.find((t: ContentNodeTranslation) => t.published);
  return t?.title ?? node.slug;
}

export function SiteHeader() {
  const { t, i18n } = useTranslation();
  const [guideNodes, setGuideNodes] = useState<ContentNodeResponse[]>([]);
  const branding = useSiteBranding();

  useEffect(() => {
    void apiService.contentNodes.getRoots()
      .then(res => setGuideNodes(res.data ?? []))
      .catch(() => {});
  }, []);
  const { loading, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { switchLanguage } = useLanguageSwitcher();
  const showFindHelpers = !user || user.role === 'EMPLOYER';
  const showPricing = !user || user.role === 'EMPLOYER';
  const homeHref = '/';

  const handleLogout = async () => {
    await logout();
    sessionStorage.removeItem('returnUrl');
    navigate('/', { replace: true });
    setIsSheetOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
        <Link to={homeHref} className="flex items-center">
          <img src={branding.logoUrl || '/logo.png'} alt="MaidLinks" className="h-9 w-auto object-contain" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to={homeHref}
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            {t('nav.home', '首頁')}
          </Link>
          {showFindHelpers && (
            <Link
              to="/candidates"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              {t('nav.findHelpers', '找外傭')}
            </Link>
          )}
          <Link
            to="/job-postings"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
          >
            {t('nav.findJobs', '找工作')}
          </Link>
          {showPricing && (
            <Link
              to="/subscription"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              {t('nav.pricing', '收費')}
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
                {t('nav.guides', '指南')}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {guideNodes.map((node) => (
                <DropdownMenuItem key={node.slug} asChild>
                  <Link to={`/resources/${node.slug}`}>{pickTitle(node, i18n.language)}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageMenu />
          {loading ? (
            <div className="h-9 w-24 animate-pulse rounded-md bg-muted" />
          ) : (
            <UserMenu />
          )}
        </div>

        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-6 w-6" />
              <span className="sr-only">{t('nav.toggleMenu', '開關導航選單')}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px] flex flex-col">
            <SheetHeader className="text-left">
              <SheetTitle asChild>
                <div className="flex items-center mb-2">
                  <img src={branding.logoUrl || '/logo.png'} alt="MaidLinks" className="h-8 w-auto object-contain" />
                </div>
              </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-4 max-h-[calc(100vh-4rem)]">
              {user && (
                <div className="flex items-center gap-3 pb-4 border-b border-border mb-2">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {((user.role === 'HELPER' ? user.helperName : undefined) ?? user.username)?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{(user.role === 'HELPER' ? user.helperName : undefined) ?? user.username}</p>
                    <p className="text-sm text-muted-foreground">
                      {user.role === 'HELPER' ? t('role.helper', '外傭') : user.role === 'ADMIN' ? t('role.admin', '管理員') : t('role.employer', '僱主')}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Link to={homeHref} className="px-2 py-2 text-base font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                  {t('nav.home', '首頁')}
                </Link>
                {showFindHelpers && (
                  <Link to="/candidates" className="px-2 py-2 text-base font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                    {t('nav.findHelpers', '找外傭')}
                  </Link>
                )}
                <Link to="/job-postings" className="px-2 py-2 text-base font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                  {t('nav.findJobs', '找工作')}
                </Link>
                {showPricing && (
                  <Link to="/subscription" className="px-2 py-2 text-base font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                    {t('nav.pricing', '收費')}
                  </Link>
                )}
                <div className="px-2 pt-1">
                  <p className="text-sm font-medium text-muted-foreground mb-1">{t('nav.guides', '指南')}</p>
                  <div className="flex flex-col gap-1 pl-2">
                    {guideNodes.map((node) => (
                      <Link key={node.slug} to={`/resources/${node.slug}`} className="py-1.5 text-base font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsSheetOpen(false)}>
                        {pickTitle(node, i18n.language)}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {user && (
                <>
                  <Separator />
                  <DashboardNav orientation="vertical-sheet" onItemClick={() => setIsSheetOpen(false)} />
                </>
              )}

              <Separator />

              <div className="flex flex-col gap-2 px-2">
                <span className="text-sm font-medium text-muted-foreground mb-1">{t('common.language', '語言')}</span>
                <div className="flex flex-wrap gap-2">
                  {SUPPORTED_LOCALES.map((locale) => (
                    <Button
                      key={locale.code}
                      variant="outline"
                      size="sm"
                      className={i18n.language === locale.code ? "font-bold underline border-primary" : ""}
                      onClick={() => {
                        switchLanguage(locale.code);
                        setIsSheetOpen(false);
                      }}
                    >
                      {locale.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between px-2 pt-2 pb-2">
                <span className="text-sm font-medium text-muted-foreground">{t('common.theme', '主題')}</span>
                <ThemeToggle />
              </div>
            </div>

            <div className="pt-4 border-t border-border mt-auto shrink-0">
              {user ? (
                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleLogout}
                >
                  {t('common.logout', '登出')}
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Button variant="outline" className="w-full" onClick={() => { navigate('/login-form'); setIsSheetOpen(false); }}>
                    {t('common.login', '登入')}
                  </Button>
                  <Button className="w-full" onClick={() => { navigate('/register'); setIsSheetOpen(false); }}>
                    {t('common.register', '免費註冊')}
                  </Button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
